import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, Loader2, Handshake, Upload, Image as ImageIcon } from 'lucide-react';
import logger from '../../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const emptyForm = { name: '', logo_url: '', category: 'institutionnel', website_url: '', order: 0 };
const CATEGORIES = [
  { value: 'institutionnel', label: 'Institutionnel' },
  { value: 'officiel', label: 'Officiel' },
  { value: 'media', label: 'Média' },
  { value: 'sponsor', label: 'Sponsor' },
];

export default function ManagePartenaires() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [mediaOpen, setMediaOpen] = useState(false);
  const [gallery, setGallery] = useState([]);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchItems = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/partenaires/all`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(res.data);
    } catch (e) { toast.error('Erreur'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('is_logo', 'true');
      const res = await axios.post(`${API}/upload/image`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      setForm({ ...form, logo_url: `${BACKEND_URL}${res.data.url}` });
      toast.success('Logo uploadé');
    } catch (err) { logger.error('Logo upload failed:', err); toast.error('Erreur lors de l\'upload'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const openMediaPicker = async () => {
    try {
      const res = await axios.get(`${API}/gallery/all`, { headers });
      setGallery(res.data);
    } catch (e) { logger.error('Gallery fetch failed:', e); toast.error('Bibliothèque inaccessible'); }
    setMediaOpen(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.logo_url) { toast.error('Nom et logo requis'); return; }
    setSaving(true);
    try {
      if (editId) {
        await axios.put(`${API}/partenaires/${editId}`, form, { headers });
        toast.success('Partenaire mis à jour');
      } else {
        await axios.post(`${API}/partenaires`, form, { headers });
        toast.success('Partenaire ajouté');
      }
      setDialogOpen(false); setForm(emptyForm); setEditId(null); fetchItems();
    } catch (e) { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const handleEdit = (item) => {
    setForm({ name: item.name, logo_url: item.logo_url, category: item.category, website_url: item.website_url || '', order: item.order || 0 });
    setEditId(item.id); setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce partenaire ?')) return;
    try { await axios.delete(`${API}/partenaires/${id}`, { headers }); toast.success('Supprimé'); fetchItems(); }
    catch (e) { toast.error('Erreur'); }
  };

  const logoSrc = (url) => url?.startsWith('/api') ? `${BACKEND_URL}${url}` : url;

  return (
    <div data-testid="manage-partenaires">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1E3A5F]">Partenaires</h1>
          <p className="text-sm text-[#6B7C93]">{items.length} partenaire(s)</p>
        </div>
        <Button onClick={() => { setForm(emptyForm); setEditId(null); setDialogOpen(true); }} className="bg-[#1E3A5F] hover:bg-[#152A45]" data-testid="add-partenaire-btn">
          <Plus className="w-4 h-4 mr-2" />Ajouter
        </Button>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-20">Logo</TableHead>
                <TableHead>Nom</TableHead>
                <TableHead>Catégorie</TableHead>
                <TableHead>Site web</TableHead>
                <TableHead className="w-20">Ordre</TableHead>
                <TableHead className="w-24">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    {item.logo_url ? (
                      <img src={logoSrc(item.logo_url)} alt="" className="h-8 w-auto max-w-[60px] object-contain" />
                    ) : (
                      <div className="w-10 h-8 rounded bg-[#F0F2F5] flex items-center justify-center"><Handshake className="w-4 h-4 text-[#94A3B8]" /></div>
                    )}
                  </TableCell>
                  <TableCell className="font-medium">{item.name}</TableCell>
                  <TableCell><span className="text-xs px-2 py-1 rounded-full bg-[#F0F2F5] capitalize">{item.category}</span></TableCell>
                  <TableCell className="text-sm text-[#6B7C93] truncate max-w-[200px]">{item.website_url || '-'}</TableCell>
                  <TableCell>{item.order}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(item)}><Pencil className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {!loading && items.length === 0 && (
            <div className="text-center py-12 text-[#6B7C93]"><Handshake className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>Aucun partenaire</p></div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>{editId ? 'Modifier' : 'Ajouter'} un partenaire</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            {/* Logo upload */}
            <div>
              <Label className="text-sm font-semibold">Logo *</Label>
              <div className="flex items-center gap-3 mt-1.5">
                {form.logo_url ? (
                  <img src={form.logo_url} alt="Logo" className="w-16 h-16 rounded-lg object-contain bg-[#F7F9FC] border border-[#E1E8F0]" />
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-[#F0F2F5] border border-dashed border-[#D1D5DB] flex items-center justify-center">
                    <ImageIcon className="w-6 h-6 text-[#94A3B8]" />
                  </div>
                )}
                <div className="flex flex-col gap-1.5">
                  <div className="flex gap-1.5">
                    <label className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1E3A5F] text-white text-xs font-medium rounded-lg cursor-pointer hover:bg-[#152A45] transition-colors">
                      {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                      {uploading ? 'Upload...' : 'Uploader'}
                      <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} disabled={uploading} data-testid="partenaire-logo-upload" />
                    </label>
                    <Button type="button" variant="outline" size="sm" onClick={openMediaPicker} className="text-xs h-auto py-1.5" data-testid="partenaire-media-picker">
                      <ImageIcon className="w-3.5 h-3.5 mr-1" />Bibliothèque
                    </Button>
                  </div>
                  {form.logo_url && (
                    <button onClick={() => setForm({ ...form, logo_url: '' })} className="text-xs text-red-500 hover:text-red-700 text-left">Supprimer le logo</button>
                  )}
                </div>
              </div>
            </div>
            <div><Label>Nom *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="partenaire-name-input" /></div>
            <div>
              <Label>Catégorie</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Site web</Label><Input value={form.website_url} onChange={(e) => setForm({ ...form, website_url: e.target.value })} placeholder="https://..." /></div>
            <div><Label>Ordre</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} /></div>
            <Button onClick={handleSave} disabled={saving} className="w-full bg-[#1E3A5F] hover:bg-[#152A45]" data-testid="partenaire-submit-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{editId ? 'Mettre à jour' : 'Ajouter'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Media Picker */}
      <Dialog open={mediaOpen} onOpenChange={setMediaOpen}>
        <DialogContent className="bg-white max-w-3xl max-h-[70vh] overflow-hidden flex flex-col">
          <DialogHeader><DialogTitle>Sélectionner un logo depuis la bibliothèque</DialogTitle></DialogHeader>
          <div className="flex-1 overflow-y-auto mt-2">
            {gallery.length === 0 ? (
              <p className="text-center py-8 text-[#94A3B8]">Aucune image dans la bibliothèque</p>
            ) : (
              <div className="grid grid-cols-5 gap-2">
                {gallery.map(img => (
                  <button
                    key={img.id}
                    onClick={() => { setForm({ ...form, logo_url: `${BACKEND_URL}${img.url}` }); setMediaOpen(false); }}
                    className="rounded-lg overflow-hidden border-2 border-transparent hover:border-[#F5A623] transition-colors"
                    data-testid={`pick-logo-${img.id}`}
                  >
                    <img src={`${BACKEND_URL}${img.url}`} alt={img.title} className="w-full h-20 object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
